import type { AnyValue } from "@/lib/rowTypes";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Users, CheckCircle2, AlertCircle, Loader2, Sparkles, ArrowRight } from "lucide-react";
import heirwiseLogomark from "@/assets/heirwise-logomark.png";
import { useAuth } from "@/hooks/useAuth";
import { useHouseholdRole } from "@/hooks/useHouseholdRole";
import { trackMetaEvent } from "@/lib/metaPixel";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { isKnownParentEmail } from "@/lib/qaAccounts";

const INVITE_STORAGE_KEY = "heirwise_invite_code";
const REFERRAL_STORAGE_KEY = "heirwise_ref_code";
export const PENDING_PLAN_STORAGE_KEY = "heirwise_pending_plan";
const PLAN_KEY_PATTERN = /^[a-z0-9_]{1,40}$/;

// Plain-language guidance for server-side password policy rejections
// (leaked/weak password protection). Policy itself is unchanged.
const friendlySignUpError = (message: string): string => {
  if (/weak|easy to guess|pwned|breach|compromised/i.test(message)) {
    return "That password is too common or has appeared in a data breach. Choose a longer, unique password you don't use anywhere else.";
  }
  if (/at least|too short|characters/i.test(message)) {
    return "Your password is too short. Use at least 6 characters.";
  }
  return message;
};

// Canonical invite code = lowercase, alphanumeric only.
// Accepts any casing/spacing/hyphens and resolves them to the same code.
const normalizeInviteCode = (raw: string): string =>
  (raw || "").toLowerCase().replace(/[^a-z0-9]/g, "");

type Mode = "signin" | "signup" | "join_family";

interface InvitePreview {
  household_name: string;
  display_name: string | null;
  invite_email: string;
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("signin");
  const [claimingInvite, setClaimingInvite] = useState(false);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { user, loading: authLoading, signIn, signUp } = useAuth();
  const { role: householdRole, loading: roleLoading } = useHouseholdRole();
  const [searchParams] = useSearchParams();

  // Safety valve: unblock the form after 6s if auth init never resolves.
  useEffect(() => {
    if (!authLoading) {
      setAuthTimedOut(false);
      return;
    }
    const t = setTimeout(() => setAuthTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, [authLoading]);

  // Pick up invite code from URL (?invite=...) or localStorage and switch
  // straight into Join Family mode.
  useEffect(() => {
    const urlCode = searchParams.get("invite");
    const stored = localStorage.getItem(INVITE_STORAGE_KEY);
    const raw = urlCode || stored;
    if (raw) {
      const normalized = normalizeInviteCode(raw);
      setInviteCode(normalized);
      setMode("join_family");
      if (urlCode) localStorage.setItem(INVITE_STORAGE_KEY, normalized);
    } else {
      const urlMode = searchParams.get("mode");
      const planParam = searchParams.get("plan");
      if (urlMode === "signup" || urlMode === "signin" || urlMode === "join_family") {
        setMode(urlMode as Mode);
      } else if (planParam) {
        // A plan selection means a new customer — never strand them on Sign In.
        setMode("signup");
      }
      if (planParam && PLAN_KEY_PATTERN.test(planParam)) {
        localStorage.setItem(PENDING_PLAN_STORAGE_KEY, planParam);
      }
    }
    // Independently capture ?ref=CODE for referral attribution. We store it
    // and apply it when the household is created (in useHousehold.createHousehold).
    const refParam = searchParams.get("ref");
    if (refParam) {
      const normalizedRef = normalizeInviteCode(refParam);
      if (normalizedRef) localStorage.setItem(REFERRAL_STORAGE_KEY, normalizedRef);
    }
  }, [searchParams]);

  // Live invite validation (debounced)
  useEffect(() => {
    if (mode !== "join_family") {
      setInvitePreview(null);
      setInviteError(null);
      return;
    }
    const code = normalizeInviteCode(inviteCode);
    if (!code || code.length < 4) {
      setInvitePreview(null);
      setInviteError(null);
      return;
    }
    setValidatingInvite(true);
    const timer = setTimeout(async () => {
      try {
        console.info("[Join Family] validating", { entered: inviteCode, normalized: code });
        const { data, error } = await supabase.rpc("validate_household_invite", {
          p_invite_code: code,
        });
        if (error) {
          console.error("[Join Family] validate RPC error", error);
          setInvitePreview(null);
          setInviteError("Could not check this invite. Try again.");
        } else if ((data as AnyValue)?.valid) {
          setInvitePreview(data as AnyValue);
          setInviteError(null);
          if (!email && (data as AnyValue).invite_email) {
            setEmail((data as AnyValue).invite_email);
          }
        } else {
          setInvitePreview(null);
          setInviteError((data as AnyValue)?.error || "Invite code not found.");
        }
      } catch (err) {
        console.error("[Join Family] validate exception", err);
        setInviteError("Could not check this invite. Try again.");
      } finally {
        setValidatingInvite(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode, mode]);

  // After login, claim any pending invite (covers Join Family signup AND
  // the case where an existing user clicks an invite link).
  useEffect(() => {
    if (!user || claimingInvite) return;
    const code = localStorage.getItem(INVITE_STORAGE_KEY);
    if (!code) return;

    const claimInvite = async () => {
      setClaimingInvite(true);
      try {
        const normalized = normalizeInviteCode(code);
        console.info("[Join Family] claiming", { stored: code, normalized });
        const { data, error } = await supabase.rpc("claim_household_invite", {
          p_invite_code: normalized,
        });
        if (error) {
          console.error("[Join Family] claim RPC error", error);
          toast.error("Couldn't link your account to the household. Please contact your parent.");
        } else if ((data as AnyValue)?.success) {
          console.info("[Join Family] claim success", { household_id: (data as AnyValue).household_id });
          toast.success(
            `Welcome! You've joined the ${(data as AnyValue).display_name || "family"} household. Your access is covered by the parent plan.`,
          );
        } else if ((data as AnyValue)?.error) {
          console.warn("[Join Family] claim rejected", (data as AnyValue).error);
          toast.error((data as AnyValue).error);
        }
      } catch (err) {
        console.error("[Join Family] claim exception", err);
      }
      localStorage.removeItem(INVITE_STORAGE_KEY);
      setClaimingInvite(false);
    };
    claimInvite();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if ((authLoading && !authTimedOut) || claimingInvite || (user && roleLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0c0a1f] text-zinc-300">
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-300" />
          <p className="text-sm">{claimingInvite ? "Joining household…" : "Opening the gateway…"}</p>
        </div>
      </div>
    );
  }

  // Once user is signed in AND any pending invite has been processed, redirect.
  if (user && !localStorage.getItem(INVITE_STORAGE_KEY)) {
    // Continue a plan chosen before authentication to the existing checkout
    // entry point (pricing), where the signed-in plan button starts checkout.
    const pendingPlan = localStorage.getItem(PENDING_PLAN_STORAGE_KEY);
    if (pendingPlan) {
      localStorage.removeItem(PENDING_PLAN_STORAGE_KEY);
      if (PLAN_KEY_PATTERN.test(pendingPlan)) {
        return <Navigate to={`/pricing?plan=${encodeURIComponent(pendingPlan)}#pricing`} replace />;
      }
    }
    return <Navigate to="/dashboard" replace />;
  }

  const isSignUp = mode === "signup" || mode === "join_family";
  const isJoinFamily = mode === "join_family";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Block submission in Join Family mode if the invite isn't valid
    if (isJoinFamily && !invitePreview) {
      toast.error(inviteError || "Enter a valid invite code to continue.");
      return;
    }

    // Persist normalized invite code BEFORE creating account so the post-signup hook
    // can claim it once the session lands.
    if (isJoinFamily) {
      const normalized = normalizeInviteCode(inviteCode);
      if (normalized) localStorage.setItem(INVITE_STORAGE_KEY, normalized);
    }

    setAuthError(null);
    setLoading(true);
    const result = isSignUp ? await signUp(email, password) : await signIn(email, password);
    const error = result.error;
    const needsEmailConfirmation = "needsEmailConfirmation" in result ? result.needsEmailConfirmation : false;
    setLoading(false);
    if (error) {
      const message = isSignUp ? friendlySignUpError(error.message) : error.message;
      setAuthError(message);
      toast.error(message);
    } else {
      if (isSignUp) {
        trackMetaEvent("CompleteRegistration", {
          email,
          customData: { method: mode },
        });
      }
      if (mode === "signup") {
        // Only promise a confirmation email when one is actually sent.
        toast.success(
          needsEmailConfirmation
            ? "Check your email to confirm your account!"
            : "Account created — you're signed in.",
        );
      } else if (mode === "join_family") {
        toast.success("Account created! Linking you to the family...");
      }
    }
  };

  const titleByMode: Record<Mode, string> = {
    signin: "Sign in",
    signup: "Create account",
    join_family: "Join Family",
  };

  // OAuth entry points are intentionally hidden for now. Handlers are intentionally
  // preserved so Google/Apple sign-in can be re-enabled without rebuilding auth.
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        toast.error(result.error.message || "Google sign-in failed.");
        setLoading(false);
        return;
      }
      if (result.redirected) return;
    } catch (err) {
      console.error("[Google sign-in] exception", err);
      toast.error("Google sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        toast.error(result.error.message || "Apple sign-in failed.");
        setLoading(false);
        return;
      }
      if (result.redirected) return;
    } catch (err) {
      console.error("[Apple sign-in] exception", err);
      toast.error("Apple sign-in failed. Please try again.");
      setLoading(false);
    }
  };
  const subtitleByMode: Record<Mode, string> = {
    signin: "Enter your credentials to continue",
    signup: "Start your Economic & Enterprise Education journey",
    join_family: "Use your parent's invite code — no separate payment needed",
  };

  return (
    <div className="relative min-h-screen w-full bg-[#0c0a1f] text-zinc-100 overflow-x-hidden flex flex-col">
      <SEO
        title="Sign in to HeirWise"
        description="Sign in or create a paid HeirWise account for Economic & Enterprise Education for teens ages 13+."
        path="/login"
      />
      <h1 className="sr-only">
        {isJoinFamily ? "Join your family on HeirWise" : isSignUp ? "Create a HeirWise account" : "Sign in to HeirWise"}
      </h1>

      {/* Ambient cinematic background — Lunimar portal */}
      <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(70,55,140,0.55),transparent_60%),radial-gradient(ellipse_at_bottom,rgba(30,40,90,0.6),transparent_55%)]" />
        <div className="absolute -top-[12%] -left-[12%] h-[60%] w-[60%] rounded-full bg-indigo-700/30 blur-[160px]" />
        <div className="absolute -bottom-[18%] -right-[12%] h-[70%] w-[70%] rounded-full bg-fuchsia-700/20 blur-[180px]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[45%] w-[85%] rounded-full bg-cyan-500/10 blur-[180px]" />
        {/* Portal ring */}
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px]">
          <div className="absolute inset-0 rounded-full border border-indigo-400/15" />
          <div className="absolute inset-10 rounded-full border border-violet-400/10" />
          <div className="absolute inset-24 rounded-full border border-cyan-400/10" />
          <div className="absolute inset-36 rounded-full bg-[conic-gradient(from_0deg,rgba(99,102,241,0.16),rgba(168,85,247,0.1),rgba(34,211,238,0.16),rgba(99,102,241,0.16))] blur-2xl opacity-60 animate-[spin_50s_linear_infinite]" />
        </div>
        {/* constellation grid */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.45)_100%)]" />
      </div>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 sm:px-8 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo + status chip */}
          <div className="flex flex-col items-center text-center mb-6">
            <Link to="/" className="inline-flex items-center gap-2 mb-4 group">
              <img
                src={heirwiseLogomark}
                alt="HeirWise"
                className="h-14 w-14 object-contain drop-shadow-[0_0_18px_rgba(139,92,246,0.55)] transition group-hover:scale-105"
              />
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[10px] uppercase tracking-[0.22em] text-indigo-200/90 backdrop-blur-md">
              <Sparkles className="w-3 h-3 text-indigo-300" />
              HeirWise · Access Gateway
            </div>
            <h2 className="mt-4 font-heading text-2xl sm:text-[1.75rem] font-semibold tracking-tight bg-gradient-to-b from-white via-zinc-200 to-zinc-500 bg-clip-text text-transparent leading-tight">
              {isJoinFamily
                ? "Join your family on HeirWise"
                : mode === "signup"
                  ? "Enter the HeirWise Gateway"
                  : "Welcome back to HeirWise"}
            </h2>
            <p className="mt-2 text-sm text-zinc-400 max-w-sm">
              {isJoinFamily
                ? "Your access is covered by your parent's household plan."
                : "Continue your teen's practical learning across money, economics, business, and entrepreneurship."}
            </p>
          </div>

          {/* Gateway pass */}
          <div className="relative">
            <div className="absolute -inset-1 rounded-[26px] bg-gradient-to-br from-indigo-500/25 via-violet-500/15 to-cyan-500/20 blur-2xl opacity-70 pointer-events-none" />
            <div className="relative rounded-[24px] p-[1px] bg-gradient-to-br from-white/25 via-white/5 to-white/15">
              <div className="relative rounded-[23px] border border-white/10 bg-[#11102a]/85 backdrop-blur-2xl shadow-2xl shadow-indigo-950/50 overflow-hidden">
                {/* corner accents */}
                <div aria-hidden className="pointer-events-none absolute inset-0">
                  <div className="absolute top-3 left-3 h-4 w-4 border-l border-t border-indigo-400/50 rounded-tl-md" />
                  <div className="absolute top-3 right-3 h-4 w-4 border-r border-t border-indigo-400/50 rounded-tr-md" />
                  <div className="absolute bottom-3 left-3 h-4 w-4 border-l border-b border-purple-400/50 rounded-bl-md" />
                  <div className="absolute bottom-3 right-3 h-4 w-4 border-r border-b border-purple-400/50 rounded-br-md" />
                </div>
                <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-80 h-40 bg-indigo-500/20 blur-3xl rounded-full" />

                <div className="relative p-6 sm:p-7">
                  {/* Mode toggle chips */}
                  <div className="grid grid-cols-3 gap-1 mb-5 rounded-xl bg-white/[0.04] border border-white/10 p-1">
                    {([
                      { id: "signin", label: "Sign in" },
                      { id: "signup", label: "Sign up" },
                      { id: "join_family", label: "Join Family", icon: Users },
                    ] as const).map((opt) => {
                      const Icon = (opt as AnyValue).icon;
                      const active = mode === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setMode(opt.id as Mode)}
                          className={
                            "text-[11px] font-semibold uppercase tracking-[0.12em] rounded-lg py-2 transition-all flex items-center justify-center gap-1 " +
                            (active
                              ? "bg-gradient-to-br from-indigo-500/80 to-violet-600/80 text-white shadow-[0_4px_18px_-6px_rgba(139,92,246,0.6)] ring-1 ring-white/20"
                              : "text-zinc-400 hover:text-zinc-100")
                          }
                        >
                          {Icon ? <Icon className="h-3 w-3" /> : null}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>

                  <p className="text-[11px] text-zinc-500 mb-4">{subtitleByMode[mode]}</p>
                  {isSignUp && (
                    <p className="text-[11px] text-zinc-500 -mt-2 mb-4">
                      You must be 13 or older to create an account.
                    </p>
                  )}

                  <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                    {isJoinFamily && (
                      <NoirField label="Family Invite Code" htmlFor="invite">
                        <NoirInput
                          id="invite"
                          value={inviteCode}
                          onChange={(e) => setInviteCode(normalizeInviteCode(e.target.value))}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData("text");
                            setInviteCode(normalizeInviteCode(pasted));
                          }}
                          placeholder="e.g. a1b2c3d4"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          className="font-mono tracking-[0.3em] uppercase"
                          required
                        />
                        <p className="text-[11px] text-zinc-500 mt-1.5">
                          Enter the code exactly as shared by your parent. Casing and spaces don't matter.
                        </p>
                        {validatingInvite && (
                          <p className="mt-2 text-[11px] text-zinc-400 flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" /> Checking invite…
                          </p>
                        )}
                        {invitePreview && !validatingInvite && (
                          <div className="mt-2 rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-2.5 flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-indigo-300 mt-0.5 shrink-0" />
                            <div className="text-[11px]">
                              <p className="font-medium text-zinc-100">
                                Joining {invitePreview.household_name}
                                {invitePreview.display_name ? ` as ${invitePreview.display_name}` : ""}
                              </p>
                              <p className="text-zinc-400">
                                Your access is covered by the parent plan — you won't be charged.
                              </p>
                            </div>
                          </div>
                        )}
                        {inviteError && !validatingInvite && (
                          <div className="mt-2 rounded-xl border border-rose-400/30 bg-rose-500/10 p-2.5 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
                            <p className="text-[11px] text-rose-200">{inviteError}</p>
                          </div>
                        )}
                      </NoirField>
                    )}

                    <NoirField label="Email" htmlFor="email">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                        <NoirInput
                          id="email"
                          type="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          className="pl-10"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                    </NoirField>

                    <NoirField label="Password" htmlFor="password">
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                        <NoirInput
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete={isSignUp ? "new-password" : "current-password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                          aria-describedby={isSignUp ? "password-requirements" : undefined}
                        />
                        <button
                          type="button"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 hover:text-zinc-200 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {isSignUp && (
                        <p id="password-requirements" className="mt-1.5 text-[11px] text-zinc-400">
                          Use at least 6 characters. Common or previously breached passwords are not accepted.
                        </p>
                      )}
                    </NoirField>

                    {authError && (
                      <div role="alert" className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-2.5 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
                        <p className="text-[12px] text-rose-200">{authError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || (isJoinFamily && !invitePreview)}
                      className="relative mt-1 w-full group disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-400 blur-xl opacity-50 group-hover:opacity-90 transition duration-500" />
                      <span className="relative flex items-center justify-center w-full bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-600 group-hover:from-indigo-400 group-hover:via-violet-400 group-hover:to-purple-500 text-white font-semibold py-3.5 rounded-xl text-sm tracking-wide transition-all shadow-[0_10px_40px_-10px_rgba(139,92,246,0.7)] ring-1 ring-white/20">
                        {loading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Please wait…</>
                        ) : (
                          <>
                            {mode === "signin" ? "Sign In" : mode === "signup" ? "Sign Up" : "Join Family"}
                            <ArrowRight className="ml-2 h-4 w-4 opacity-80 group-hover:translate-x-0.5 transition-transform" />
                          </>
                        )}
                      </span>
                    </button>
                  </form>

                  <div className="text-center mt-5 space-y-2">
                    <Link
                      to="/forgot-password"
                      className="text-[12px] text-zinc-400 hover:text-indigo-200 underline-offset-4 hover:underline"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Secondary access link */}
          <p className="mt-5 text-center text-[12px] text-zinc-400">
            Need access?{" "}
            <Link to="/pricing" className="text-indigo-300 hover:text-indigo-200 underline-offset-4 hover:underline font-medium">
              See plans
            </Link>
          </p>

          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            Plans from $24.99/mo · Parent-managed · Ages 13+
          </p>


        </motion.div>
      </main>
    </div>
  );
};

/* ─── Noir field primitives ─────────────────────────────────────────────── */

function NoirField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="block text-[10px] font-semibold text-zinc-400 uppercase tracking-[0.14em]"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function NoirInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { className = "", ...rest } = props;
  return (
    <input
      {...rest}
      className={
        "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-zinc-100 " +
        "placeholder:text-white/60 focus:outline-none focus:border-indigo-400/60 focus:ring-2 focus:ring-indigo-500/30 " +
        "transition-all " +
        className
      }
    />
  );
}

export default Login;
